// 行為別売上モバイル page (/m/a-kbrev) — 2026-07-22 新設 (PC「行為別売上」タブのモバイル要約)
// - 主要4カード: 入院収入(合計) / 入院料小計 / 手術小計 / 処置小計 (当月実績 + 前年比 + 13ヶ月 sparkline)
// - 全行為 (集計区分) の当月実績 + 前年 + 前年比 リスト (色 = 前年同月比)
// - 診療科セレクタ (病院全体=総合計 / 各科) ・ 月送り ←→
// 源 = /api/query/kb_revenue (fact_kb_revenue_long・入院のみ・合計=入院収入=KPIと一致・公開endpoint)
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { useMeta } from "../lib/mobileApi";
import { MobileShell } from "../components/MobileShell";
import { StatPill, sentimentFromDelta, SENTIMENT_BG, SENTIMENT_RING } from "../components/StatBadge";
import { Sparkline } from "../components/Sparkline";
import { fmtByUnitMain } from "../../lib/format";
import type { KbRevenueResponse } from "@shared/api";

// 行為 (集計区分) の表示順 (合計を先頭 + PC KbRevenuePanel と同じ順)。合計 = 入院収入 (全行為の総額)。
const ACT_ORDER = ["合計", "入院料小計", "手術小計", "処置小計", "検査小計", "注射小計", "投薬小計", "画像診断小計", "医学管理", "食事小計", "自費小計", "その他小計"];
const CARD_ACTS = ["合計", "入院料小計", "手術小計", "処置小計"];
// 表示ラベル (合計 → 入院収入)。それ以外は「〜小計」の「小計」を落とす。
const actLabel = (a: string): string => (a === "合計" ? "入院収入" : a.replace(/小計$/, ""));

type Cell = { a: number | null; py: number | null };

// 千円値 → 億/万/円 に自動スケール ({value, unit})
const fmtSen = (sen: number | null): { value: string; unit: string } =>
  sen == null ? { value: "—", unit: "千円" } : fmtByUnitMain(sen * 1000, "円");

// 前年同月比 (delta) → セル背景 5 階調
function cellBg(pct: number | null): string {
  if (pct == null) return "";
  if (pct >= 0.10) return "bg-emerald-300";
  if (pct >= 0.05) return "bg-emerald-100";
  if (pct >= -0.05) return "";
  if (pct >= -0.10) return "bg-red-100";
  return "bg-red-300";
}

export function KbRevenueMobile() {
  const { data: meta } = useMeta();
  const [asOfMonth, setAsOfMonth] = useState<string | null>(null);
  const [dept, setDept] = useState<string>("総合計");

  const periodTo = asOfMonth ?? meta?.dateMax ?? "";
  const periodFrom = periodTo ? monthShift(periodTo, -23) : "";

  const { data, isLoading } = useQuery<KbRevenueResponse>({
    queryKey: ["kb-revenue-mobile", periodFrom, periodTo],
    queryFn: () => api.kbRevenue({ periodFrom, periodTo }),
    placeholderData: (p) => p,
    enabled: !!meta && !!periodTo,
  });

  // index: 診療科 -> 集計区分 -> ym -> Cell、 + 科一覧 / 月一覧
  const { idx, depts, months } = useMemo(() => {
    const idx = new Map<string, Map<string, Map<string, Cell>>>();
    const mset = new Set<string>();
    const dset = new Set<string>();
    for (const r of data?.rows ?? []) {
      const ym = r.date.slice(0, 7);
      mset.add(ym);
      dset.add(r.診療科);
      if (!idx.has(r.診療科)) idx.set(r.診療科, new Map());
      const byAct = idx.get(r.診療科)!;
      if (!byAct.has(r.集計区分)) byAct.set(r.集計区分, new Map());
      byAct.get(r.集計区分)!.set(ym, { a: r.実績 ?? null, py: r.前年実績 ?? null });
    }
    const months = [...mset].sort();
    // 科一覧: 総合計を先頭、 残りは合計実績の降順
    const depts = [...dset].sort((x, y) => {
      if (x === "総合計") return -1;
      if (y === "総合計") return 1;
      const sx = idx.get(x)?.get("合計"); const sy = idx.get(y)?.get("合計");
      const vx = sx ? [...sx.values()].reduce((s, c) => s + (c.a ?? 0), 0) : 0;
      const vy = sy ? [...sy.values()].reduce((s, c) => s + (c.a ?? 0), 0) : 0;
      return vy - vx;
    });
    return { idx, depts, months };
  }, [data]);

  if (!meta) return <div className="p-6 text-sm text-slate-400">読み込み中…</div>;

  const asOfYm = periodTo.slice(0, 7);
  const dispMonths = months.length >= 13 ? months.slice(-13) : months;
  const canPrev = monthShift(periodTo, -1).slice(0, 7) >= meta.dateMin.slice(0, 7);
  const canNext = monthShift(periodTo, +1).slice(0, 7) <= meta.dateMax.slice(0, 7);

  // 選択中の科が data に無ければ 総合計 に戻す (月移動で消える等の保険)
  const activeDept = idx.has(dept) ? dept : "総合計";
  const byAct = idx.get(activeDept);
  const deptLabel = activeDept === "総合計" ? "🏥 病院全体" : activeDept;

  // 全行為リスト (当月実績のある行のみ、 ACT_ORDER 順)
  const actList = ACT_ORDER.filter((a) => byAct?.has(a));

  return (
    <MobileShell
      title="💴 行為別売上"
      prototypeId="A"
      prototypeName="行為別売上"
      asOfMonth={periodTo}
      onPrevMonth={() => setAsOfMonth(monthShift(periodTo, -1))}
      onNextMonth={() => setAsOfMonth(monthShift(periodTo, +1))}
      canPrevMonth={canPrev}
      canNextMonth={canNext}
    >
      <div className="flex items-center gap-1.5 text-[10px] text-slate-500 flex-wrap">
        <span className="bg-slate-100 px-1.5 py-0.5 rounded">入院のみ / 合計 = 入院収入</span>
        <label className="ml-auto flex items-center gap-1">
          <span className="text-slate-400">診療科</span>
          <select
            value={activeDept}
            onChange={(e) => setDept(e.target.value)}
            className="border border-slate-300 rounded px-1.5 py-1 bg-white text-[11px] max-w-[9rem]"
          >
            {depts.map((d) => (
              <option key={d} value={d}>{d === "総合計" ? "病院全体" : d}</option>
            ))}
          </select>
        </label>
      </div>

      <section>
        <div className="text-xs text-slate-500 mb-1">
          {asOfYm} 主要行為 <span className="text-slate-400">({deptLabel})</span>
          {isLoading && <span className="ml-2 text-[9px] text-slate-400">loading…</span>}
        </div>
        <div className="grid grid-cols-2 gap-2">
          {CARD_ACTS.map((a) => (
            <KbCard key={a} label={actLabel(a)} byMonth={byAct?.get(a)} dispMonths={dispMonths} />
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-xs font-semibold text-slate-700 mb-1">全行為 (当月・千円) <span className="text-slate-400 font-normal">色 = 前年同月比</span></h2>
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="grid grid-cols-[1fr_auto_auto] gap-2 px-3 py-2 bg-slate-100 text-[10px] font-semibold text-slate-600">
            <div>行為</div>
            <div className="text-right">当月</div>
            <div className="text-right w-14">前年比</div>
          </div>
          {actList.map((a) => {
            const c = byAct?.get(a)?.get(asOfYm) ?? { a: null, py: null };
            const yoy = (c.a != null && c.py != null && c.py !== 0) ? c.a / c.py : null;
            const pct = yoy != null ? yoy - 1 : null;
            const m = fmtSen(c.a);
            return (
              <div key={a} className="grid grid-cols-[1fr_auto_auto] gap-2 px-3 py-1.5 text-xs border-b border-slate-100 last:border-b-0 items-center">
                <div className={`${a === "合計" ? "font-bold text-slate-900" : "text-slate-800"}`}>{actLabel(a)}</div>
                <div className="text-right tabular-nums text-slate-800 whitespace-nowrap">
                  {m.value}<span className="text-[9px] text-slate-400 ml-0.5">{m.unit === "千円" ? "" : m.unit}</span>
                </div>
                <div className={`text-right tabular-nums font-semibold w-14 rounded ${cellBg(pct)}`}>
                  {yoy != null ? `${(yoy * 100).toFixed(0)}%` : "—"}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <p className="text-[10px] text-slate-400 text-center px-4 leading-relaxed">
        科別×月別マトリクス・12ヶ月移動平均は PC 版へ。
      </p>
    </MobileShell>
  );
}

// 主要行為カード: 当月(末尾月)実績 + 前年比 + 実績の 13ヶ月 sparkline
function KbCard({ label, byMonth, dispMonths }: {
  label: string; byMonth: Map<string, Cell> | undefined; dispMonths: string[];
}) {
  const series = dispMonths.map((ym) => byMonth?.get(ym) ?? { a: null, py: null });
  const last = series[series.length - 1] ?? { a: null, py: null };
  const cur = last.a, py = last.py;
  const yoyDelta = (cur != null && py != null && py !== 0) ? (cur - py) / Math.abs(py) : null;
  const sent = sentimentFromDelta(yoyDelta, "higher_is_better");
  const sparkValues = series.map((s) => s.a ?? 0);
  const sparkValid = series.filter((s) => s.a != null).length;
  const m = fmtSen(cur);

  return (
    <div className={`p-2.5 rounded-lg ring-1 ${SENTIMENT_RING[sent]} ${SENTIMENT_BG[sent]}`}>
      <div className="text-[11px] text-slate-600 font-medium leading-tight">{label}</div>
      <div className="mt-1 flex items-baseline gap-0.5">
        <span className="text-xl font-bold text-slate-900 tabular-nums">{m.value}</span>
        <span className="text-[9px] text-slate-400">{m.unit}</span>
      </div>
      {sparkValid > 1 && (
        <div className="mt-0.5 -mx-1">
          <Sparkline values={sparkValues} width={140} height={20} showLast />
        </div>
      )}
      <div className="mt-1">
        <StatPill label="前年" value={yoyDelta != null ? `${yoyDelta >= 0 ? "+" : ""}${(yoyDelta * 100).toFixed(1)}%` : "—"} sentiment={sent} size="xs" />
      </div>
    </div>
  );
}

function monthShift(d: string, delta: number): string {
  const [y, m, day] = d.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1 + delta, day));
  return [dt.getUTCFullYear(), String(dt.getUTCMonth() + 1).padStart(2, "0"), String(dt.getUTCDate()).padStart(2, "0")].join("-");
}
